/**
 * The catalogue of report types and the workbook discovery built on it.
 *
 * It lives downstream of both the report modules and `helpers/common`: this file imports every
 * report runner, and nothing a report imports may import this one. A report that needed its own
 * catalogue entry would put the cycle straight back, which is why each report owns its own default
 * options rather than reading them back out of here.
 */
import Logger from "../../../clients/sentry";
import { handleError, serializeErrorForWarning } from "../../../errors";
import { columnNames, rowNames } from "../helpers/rangeNames";
import { excelNamesMatch } from "../../workbook/columns";
import {
  alternatesDefaultOptions,
  estimateDefaultOptions,
  fetchAlternateItemsForReport,
  fetchEstimateItemsForReport,
  generateAlternatesItemsReport,
  generateEstimateReport,
} from "../estimate";
import { fetchAlternatesWithVMData, generateVMSummaryReport, vmSummaryDefaultOptions } from "../vmSummary";
import {
  fetchGCGRLineItemsWithGroups,
  fetchGCGRStaffWithDetails,
  gcStaffDefaultOptions,
  gcgrLineItemsDefaultOptions,
  generateGCGRLineReport,
  generateGcStaffReport,
} from "../gcgr";
import { estimateSOVDefaultOptions, fetchEstimateLineItemsForReport, generateEstimateSOV } from "../sov";
import { optionsFromProperties } from "../../workbook/cache";
import { fetchBidTabsWithScopeData, generateContractingSOV } from "../contractingSOV";
import { areaSheetDefaultOptions, fetchFloorAreasForReport } from "../areaSheet";
import { fetchUnitsWithUseGroupName, unitMixDefaultOptions } from "../unitMix";
import { fetchRoomsWithUseGroupName, roomsDefaultOptions } from "../rooms";
import { track } from "../../../clients/mixpanel";
import { WORKSHEET_CUSTOM_PROPERTIES, getSheetsByReportType } from "../../workbook/customProperties";
import { generateAreaSheetReportsFromTemplate } from "../templates/areaSheet";
import { generateRoomsReportsFromTemplate } from "../templates/rooms";
import { generateUnitMixReportsFromTemplate } from "../templates/unitMix";
import { alternatesSOVDefaultOptions, generateAlternatesSOVReport } from "../alternatesSOV";
import { fetchMarkupItemsForReport, generateMarkupsReport, markupsDefaultOptions } from "../markups";
import BaseError from "../../../errors/BaseError";
import {
  getAlternatesEstimateOptionDefinitions,
  getAlternatesOptionDefinitions,
  getEstimateOptionDefinitions,
  getEstimateSOVOptionDefinitions,
  getMarkupsOptionDefinitions,
  getSimpleCheckboxDefinitions,
} from "../helpers/reportOptions";
import { reportHasData } from "../api/index";
import { printFormattedData } from "../helpers/print";
import { ReportDefinition, ReportTemplate, hasMultiSheetRunner, hasSingleSheetRunner } from "./index";
import { AREA_SHEET_REPORT_NAME, ROOMS_REPORT_NAME, UNIT_MIX_REPORT_NAME } from "./reportNames";

export const reportTypes: Record<string, ReportDefinition> = {
  vmSummary: {
    group: "estimate",
    name: "VM Summary",
    options: vmSummaryDefaultOptions,
    optionDefinitions: getAlternatesOptionDefinitions(vmSummaryDefaultOptions),
    identifyingNamedRange: rowNames.vmSummary,
    reportDataSource: fetchAlternatesWithVMData,
    reportRunner: generateVMSummaryReport,
    staleChecks: ["alternates"],
  },
  estimate: {
    group: "estimate",
    name: "Estimate",
    options: estimateDefaultOptions,
    optionDefinitions: getEstimateOptionDefinitions(estimateDefaultOptions),
    identifyingNamedRange: rowNames.lineItems,
    reportDataSource: fetchEstimateItemsForReport,
    reportRunner: generateEstimateReport,
  },
  estimateSOV: {
    group: "estimate",
    name: "Estimate SOV",
    options: estimateSOVDefaultOptions,
    optionDefinitions: getEstimateSOVOptionDefinitions(estimateSOVDefaultOptions),
    identifyingNamedRange: rowNames.estimateSOV,
    reportDataSource: fetchEstimateLineItemsForReport,
    reportRunner: generateEstimateSOV,
  },
  alternates: {
    group: "estimate",
    name: "VM Decisions",
    options: alternatesDefaultOptions,
    optionDefinitions: getAlternatesEstimateOptionDefinitions(alternatesDefaultOptions),
    identifyingNamedRange: rowNames.alternateItems,
    reportDataSource: fetchAlternateItemsForReport,
    reportRunner: generateAlternatesItemsReport,
    staleChecks: ["alternates"],
  },
  gcStaff: {
    group: "gcgr",
    name: "GC Staff",
    options: gcStaffDefaultOptions,
    optionDefinitions: getSimpleCheckboxDefinitions(gcStaffDefaultOptions),
    identifyingNamedRange: rowNames.gcgrStaffItems,
    reportDataSource: fetchGCGRStaffWithDetails,
    reportRunner: generateGcStaffReport,
  },
  gcgrLineItems: {
    group: "gcgr",
    name: "GCGR Line Items",
    options: gcgrLineItemsDefaultOptions,
    optionDefinitions: getSimpleCheckboxDefinitions(gcgrLineItemsDefaultOptions),
    identifyingNamedRange: rowNames.gcgrLineItems,
    reportDataSource: fetchGCGRLineItemsWithGroups,
    reportRunner: generateGCGRLineReport,
    staleChecks: ["gcgrNonstaff"],
  },
  contractingSOV: {
    group: "contracting",
    name: "Contracting SOV",
    identifyingNamedRange: rowNames.contractingSOV,
    reportDataSource: fetchBidTabsWithScopeData,
    reportRunner: generateContractingSOV,
  },
  areaSheet: {
    group: "estimate",
    name: AREA_SHEET_REPORT_NAME,
    options: areaSheetDefaultOptions,
    optionDefinitions: getSimpleCheckboxDefinitions(areaSheetDefaultOptions),
    identifyingNamedRange: rowNames.floorAreaItems,
    reportDataSource: fetchFloorAreasForReport,
    reportRunner: generateAreaSheetReportsFromTemplate,
    splitByKey: "floor_group",
    staleChecks: ["area"],
  },
  alternatesSOV: {
    group: "estimate",
    name: "VM Decisions SOV",
    options: alternatesSOVDefaultOptions,
    optionDefinitions: getAlternatesOptionDefinitions(alternatesSOVDefaultOptions),
    identifyingNamedRange: rowNames.alternatesSOV,
    reportDataSource: fetchAlternateItemsForReport,
    reportRunner: generateAlternatesSOVReport,
    staleChecks: ["alternates"],
  },
  markups: {
    group: "estimate",
    name: "Markups",
    options: markupsDefaultOptions,
    optionDefinitions: getMarkupsOptionDefinitions(markupsDefaultOptions),
    identifyingNamedRange: rowNames.markups,
    reportDataSource: fetchMarkupItemsForReport,
    reportRunner: generateMarkupsReport,
  },
  unitMix: {
    group: "estimate",
    name: UNIT_MIX_REPORT_NAME,
    options: unitMixDefaultOptions,
    identifyingNamedRange: rowNames.unitMixItems,
    reportDataSource: fetchUnitsWithUseGroupName,
    reportRunner: generateUnitMixReportsFromTemplate,
    splitByKey: "estimate_use_group",
    staleChecks: ["units", "area"],
  },
  rooms: {
    group: "estimate",
    name: ROOMS_REPORT_NAME,
    options: roomsDefaultOptions,
    identifyingNamedRange: rowNames.roomItems,
    reportDataSource: fetchRoomsWithUseGroupName,
    reportRunner: generateRoomsReportsFromTemplate,
    splitByKey: "estimate_use_group",
    staleChecks: ["rooms"],
  },
};

/**
 * The first report type whose identifying name the sheet has. A name whose range was deleted stays
 * in the sheet's list as an error, so only names that still point at a range count.
 */
const reportTypeFromNames = (names: Excel.NamedItem[]): ReportDefinition | null => {
  const rangeNames = names.filter((name) => name.type === Excel.NamedItemType.range).map((name) => name.name);
  return (
    Object.values(reportTypes).find((reportType) =>
      rangeNames.some((name) => excelNamesMatch(name, reportType.identifyingNamedRange))
    ) ?? null
  );
};

export const discoverReportTemplates = async (): Promise<ReportTemplate[]> => {
  const worksheetNames = await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();
    return worksheets.items.map((w) => w.name);
  });

  // TODO: Investigate in a follow up PR, if we can safely bring in promise.all into this function without breaking the Excel.run context.
  const perSheet: Array<{
    name: string;
    options: Record<string, unknown>;
    reportType: ReportDefinition | null;
    props: Map<string, string>;
  }> = [];
  for (const name of worksheetNames) {
    const result = await Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getItem(name);
      try {
        // One round trip per sheet: its names say what report it is, its properties hold its options.
        const names = worksheet.names.load("items/name,items/type");
        const properties = worksheet.customProperties.load("items/key,items/value");
        await context.sync();
        const options = optionsFromProperties(properties.items);
        const reportType = reportTypeFromNames(names.items);
        const props = new Map(properties.items.map((property) => [property.key, property.value]));
        return { name, options, reportType, props };
      } catch {
        return null; // skip unreadable sheets
      }
    });
    if (result !== null) perSheet.push(result);
  }

  const templateSheets: ReportTemplate[] = [];

  for (const { name, options: rawOptions, reportType, props } of perSheet) {
    const isTemplate = props.get(WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE) === "true";

    if (reportType && !isTemplate) {
      const defaultOptions = reportType.options || {};

      const options = rawOptions
        ? Object.keys(defaultOptions).reduce(
            (validOptions, key) => {
              validOptions[key] = rawOptions[key] !== undefined ? rawOptions[key] : defaultOptions[key];
              return validOptions;
            },
            {} as Record<string, any>
          )
        : { ...defaultOptions };

      const multiSheetReportName = props.get(WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE);
      const worksheetName = multiSheetReportName ? multiSheetReportName : name;
      const reportDataSource = reportType.reportDataSource;
      const dataNamedRange = reportType.identifyingNamedRange;

      // check if worksheetName exists before pushing to templateSheets to prevent dupes
      if (!templateSheets.some((template) => template.worksheetName === worksheetName)) {
        const template: ReportTemplate = {
          worksheetName,
          reportType,
          reportDataSource,
          dataNamedRange,
          options,
          reportRunner: reportType.reportRunner,
          splitByKey: reportType.splitByKey,
        };

        templateSheets.push(template);
      }
    }
  }

  return templateSheets;
};

/** Ordered fallback list for report start range resolution. Shared by getReportStartRange and printFormattedData so read/write use the same range. */

const handleNoDataWorksheet = async (
  context: Excel.RequestContext,
  worksheet: Excel.Worksheet,
  dataNamedRange: string,
  reportName: string
): Promise<void> => {
  const namedRanges = worksheet.names.load("items/name");
  await context.sync();

  const data = namedRanges.items.find((n) => excelNamesMatch(n.name, dataNamedRange));
  const useGroups = namedRanges.items.find((n) => excelNamesMatch(n.name, `\\${columnNames.useGroups}`));
  const crossTab = namedRanges.items.find((n) => excelNamesMatch(n.name, `\\${columnNames.crossTab}`));
  const alternates = namedRanges.items.find((n) => excelNamesMatch(n.name, `\\${columnNames.alternates}`));
  const markupsNamedRanges = namedRanges.items.filter((n) => n.name.toLowerCase().endsWith("markups"));

  const useGroupsRange = useGroups?.getRangeOrNullObject();
  const crossTabRange = crossTab?.getRangeOrNullObject();
  const alternatesRange = alternates?.getRangeOrNullObject();
  const dataRange = data?.getRangeOrNullObject();
  await context.sync();

  for (const range of [useGroupsRange, crossTabRange, alternatesRange]) {
    if (range && !range.isNullObject) range.clear(Excel.ClearApplyTo.contents);
  }

  if (dataRange && !dataRange.isNullObject) {
    dataRange.load("rowIndex, columnIndex, columnCount");
    await context.sync();

    // pass in 2d array with 3 rows with second row first column containing message
    const messageArray: (string | number | null)[][] = [
      new Array(dataRange.columnCount).fill(null),
      new Array(dataRange.columnCount).fill(null),
      new Array(dataRange.columnCount).fill(null),
    ];

    messageArray[1][0] = `There is no ${reportName} data to display`;

    await printFormattedData({
      dataArray: messageArray,
      worksheet,
      insertAtRangeName: dataNamedRange,
      startColumnIndex: dataRange.columnIndex,
    });

    // add formatting to the message row
    const messageCell = worksheet.getCell(dataRange.rowIndex + 1, dataRange.columnIndex);
    messageCell.format.horizontalAlignment = "Left";

    // clear any markups named ranges
    if (markupsNamedRanges.length > 0 && dataNamedRange !== rowNames.markups) {
      for (const markupsRangeItem of markupsNamedRanges) {
        const markupsNamedRange = markupsRangeItem.name;
        // These are matched by name alone, so a name whose target was deleted is still in the collection.
        // printFormattedData throws on one, which would abandon the rest of this sheet's no-data treatment.
        try {
          markupsRangeItem.getRange().load("address");
          await context.sync();
        } catch {
          continue;
        }
        await printFormattedData({
          dataArray: [],
          worksheet,
          insertAtRangeName: markupsNamedRange,
          startColumnIndex: dataRange.columnIndex,
        });
      }
    }
  }

  await context.sync();
};

/**
 * Empties report sheets and leaves the standard no-data message in place of their data. Removing a
 * datasource is the one way a report loses its data without anything else noticing: refreshAllReports
 * applies the same treatment, but only ever runs on a load.
 */

export const clearReportWorksheets = async (
  /** Blanks every report when omitted. */
  shouldClear?: (template: ReportTemplate) => boolean
): Promise<void> => {
  const templates = (await discoverReportTemplates()).filter((template) => shouldClear?.(template) ?? true);

  // One Excel.run per template, matching refreshAllReports: batching them across templates has
  // broken the context here before.
  for (const template of templates) {
    // Per template, as refreshAllReports does: a sheet whose named range the writer cannot resolve
    // throws, and aborting here would take every remaining report with it — and every caller, all of
    // which are teardown. Project removal was unreachable for one such sheet.
    try {
      await Excel.run(async (context) => {
        if (hasMultiSheetRunner(template)) {
          const sheets = await getSheetsByReportType(context, template.reportType.name);
          for (const sheetInfo of sheets) {
            await handleNoDataWorksheet(
              context,
              sheetInfo.worksheet,
              template.dataNamedRange,
              template.reportType.name
            );
          }
          await context.sync();
          return;
        }

        const worksheet = context.workbook.worksheets.getItemOrNullObject(template.worksheetName);
        await context.sync();
        if (worksheet.isNullObject) return;

        await handleNoDataWorksheet(context, worksheet, template.dataNamedRange, template.reportType.name);
        await context.sync();
      });
    } catch (error) {
      // EXCEPTION: Logger error allowed here — caught per template so one bad sheet cannot abort the
      // rest of the clear, which leaves nothing for a caller to log.
      //
      // Context goes to Logger, not handleError: that returns domain errors untouched and drops its
      // metadata, which is how the reported failure reached us naming a range but no sheet.
      const err = handleError(error, { context: "clearReportWorksheets" });
      Logger.error("Failed to clear report worksheet", err, {
        reportType: template.reportType.name,
        worksheetName: template.worksheetName,
        dataNamedRange: template.dataNamedRange,
      });
    }
  }
};

export const refreshAllReports = async (knownTemplates?: ReportTemplate[]): Promise<number> => {
  const start = performance.now();
  const templates = knownTemplates ?? (await discoverReportTemplates());
  const reportCount = templates.length;
  if (reportCount === 0) {
    return 0;
  }

  // TODO: Investigate in a follow up PR, if we can safely bring in promise.all into this function without breaking the Excel.run context.
  const results: Array<Error | null> = [];
  for (const template of templates) {
    try {
      await Excel.run(async (context) => {
        // Check if report has data before refreshing
        const hasTableData = await reportHasData(template.reportDataSource);

        // If table data exists, refresh the report
        if (hasTableData) {
          // If multi-sheet report
          if (hasMultiSheetRunner(template)) {
            await template.reportRunner();
            return;
          }

          // If single-sheet report
          const worksheet = context.workbook.worksheets.getItemOrNullObject(template.worksheetName);
          await context.sync();

          if (worksheet.isNullObject) {
            Logger.warn("Worksheet not found for report", {
              worksheetName: template.worksheetName,
              reportType: template.reportType.name,
            });
            return;
          }

          if (hasSingleSheetRunner(template)) {
            await template.reportRunner(worksheet);
            return;
          }

          Logger.warn("No report runner found for report", { reportType: template.reportType.name });
        } else {
          // If no table data, clean the report
          // If multi-sheet report
          if (hasMultiSheetRunner(template)) {
            const sheets = await getSheetsByReportType(context, template.reportType.name);
            if (sheets.length > 0) {
              for (const sheetInfo of sheets) {
                const worksheet = sheetInfo.worksheet;
                await handleNoDataWorksheet(context, worksheet, template.dataNamedRange, template.reportType.name);
              }
              await context.sync();
            }
            return;
          }

          // If single-sheet report
          const worksheet = context.workbook.worksheets.getItemOrNullObject(template.worksheetName);
          await context.sync();

          if (worksheet.isNullObject) {
            Logger.warn("Worksheet not found for report", {
              worksheetName: template.worksheetName,
              reportType: template.reportType.name,
            });
            return;
          }

          await handleNoDataWorksheet(context, worksheet, template.dataNamedRange, template.reportType.name);
          await context.sync();
        }
      });
      results.push(null);
    } catch (error) {
      const err = handleError(error, {
        worksheetName: template.worksheetName,
        reportType: template.reportType.name,
        context: "refreshAllReports",
      });
      // handleError returns domain errors untouched, dropping the metadata above -- which is why the
      // aggregation below reported "Unknown Sheet". Carry the sheet on the error itself.
      if (err instanceof BaseError) {
        err.info = { worksheetName: template.worksheetName, reportType: template.reportType.name, ...err.info };
      }
      results.push(err);
    }
  }

  const errors = results.filter((result): result is NonNullable<typeof result> => Boolean(result));

  if (errors.length > 0) {
    console.error("refreshAllReports encountered errors:", errors);
    // ???: Throw if all reports failed?
    // if (reportCount === errors.length) {
    //   throw new ValidationError("All reports failed to refresh.");
    // }
    // Breadcrumb for partial failures; generic Error retained for aggregated context
    const aggregatedError = new Error(
      errors
        .map((e) => {
          const worksheetName =
            e instanceof BaseError && e.info?.worksheetName ? e.info.worksheetName : "Unknown Sheet";
          return `${worksheetName} - ${e.message}`;
        })
        .join("; ")
    );
    Logger.warn(
      "Errors occurred while refreshing reports",
      serializeErrorForWarning(aggregatedError, {
        errorCount: errors.length,
      })
    );
  }

  const end = performance.now();
  const totalDuration = (end - start) / 1000;
  track("excel.reports.refreshAll", {
    loadTime: totalDuration,
    reportCount: templates.length,
  });

  return reportCount;
};
